import { useUnsavedChanges } from '@/hooks/useUnsavedChanges';
import { useEffect, useMemo, useState } from 'react';
import {
  studyTypeRequiredLayers, mapy,
  type StudyTypeRequiredLayerInput,
} from '@/api/endpoints';
import type { StudyTypeRequiredLayer, MapLayer } from '@/types';
import { Icon } from '@/components/Icon';
import { SearchableSelect } from '@/components/SearchableSelect';

/** Typy opracowań — kody zgodne z backendowym StudyType.name(). */
const STUDY_TYPES = [
  { code: 'OP_WOD_3B', label: '3b — Operat w-p (pobór wód)' },
  { code: 'OP_WOD_3A', label: '3a — Operat w-p (urządzenie wodne)' },
  { code: 'OP_WOD_3C', label: '3c — Operat w-p (odprowadzenie)' },
  { code: 'PRG_1A',    label: '1a — PRG (wykonanie otworu)' },
  { code: 'PRG_1B',    label: '1b — PRG (likwidacja)' },
  { code: 'PRG_1C',    label: '1c — PRG (rekonstrukcja)' },
  { code: 'KIP_2',     label: '2 — Karta informacyjna przedsięwzięcia' },
  { code: 'DOK_HYDRO_4A', label: '4a — Dok. hydrogeol. (zasoby eksploatacyjne)' },
  { code: 'DOK_HYDRO_4B', label: '4b — Dok. hydrogeol. (wydajność otworów)' },
  { code: 'DOK_HYDRO_4C', label: '4c — Dok. hydrogeol. (strefy ochronne)' },
  { code: 'DOK_LIKW_5',   label: '5 — Dok. geol. z likwidacji otworu' },
  { code: 'ANALIZA_RYZYKA_6', label: '6 — Analiza ryzyka' },
  { code: 'PLAN_BEZP_7',      label: '7 — Plan bezpieczeństwa wody' },
  { code: 'PLAN_RUCHU_8',     label: '8 — Plan ruchu' },
];

/**
 * Admin: słownik warstw WMS/WMTS wymaganych/zalecanych per typ opracowania. Wskazuje, które
 * krajowe usługi przeglądania trzeba wykorzystać przy danym dokumencie (widoczne na karcie
 * opracowania i auto-włączane w kreatorze mapy).
 */
export function StudyTypeRequiredLayersAdmin() {
  const [studyType, setStudyType] = useState<string>('OP_WOD_3B');
  const [rows, setRows] = useState<StudyTypeRequiredLayer[] | null>(null);
  const [catalog, setCatalog] = useState<MapLayer[]>([]);
  const [catalogErr, setCatalogErr] = useState(false);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [creating, setCreating] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const reload = () => {
    setErr(null);
    setRows(null);
    studyTypeRequiredLayers.list(studyType, includeInactive)
      .then(setRows)
      .catch(e => setErr(e?.message ?? String(e)));
  };

  useEffect(() => {
    mapy.layers()
      .then(l => { setCatalog(l); setCatalogErr(false); })
      .catch(() => { setCatalog([]); setCatalogErr(true); });
  }, []);
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [studyType, includeInactive]);

  const onDeactivate = async (r: StudyTypeRequiredLayer) => {
    if (!confirm(`Usunąć powiązanie warstwy „${r.layerName}" z typem ${studyType}?`)) return;
    try { await studyTypeRequiredLayers.delete(r.id); reload(); }
    catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
  };

  const onToggleField = async (r: StudyTypeRequiredLayer, patch: Partial<StudyTypeRequiredLayerInput>) => {
    try { await studyTypeRequiredLayers.update(r.id, patch); reload(); }
    catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
  };

  return (
    <div className="max-w-[1500px]">
      <header className="flex items-end justify-between mb-5 gap-4 flex-wrap">
        <div>
          <h1>Warstwy WMS/WMTS wymagane per typ</h1>
          <div className="text-ink-400 text-[13px]">
            Wskaż, które usługi przeglądania (WMS/WMTS z bazy warstw) są wymagane lub zalecane
            dla danego typu opracowania. Warstwy pojawią się na karcie opracowania i będą
            auto-włączane w kreatorze mapy.
          </div>
        </div>
        <div className="flex gap-2 items-end">
          <label className="text-[12px] text-ink-700 flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={includeInactive} onChange={e => setIncludeInactive(e.target.checked)}/>
            Pokaż usunięte
          </label>
          <button onClick={() => setCreating(true)} className="btn btn-primary btn-sm">
            <Icon name="plus" className="w-3.5 h-3.5"/> Przypisz warstwę
          </button>
        </div>
      </header>

      <div className="mb-3 flex items-center gap-2">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-ink-500">Typ opracowania:</span>
        <SearchableSelect value={studyType} onChange={setStudyType} className="max-w-[380px]">
          {STUDY_TYPES.map(t => <option key={t.code} value={t.code}>{t.label}</option>)}
        </SearchableSelect>
      </div>

      {err && <div className="text-red-500 mb-3 text-[12px]">Błąd: {err}</div>}

      <div className="card">
        <div className="card-body !p-0">
          {!rows && <div className="p-4 text-ink-400">Ładowanie…</div>}
          {rows && rows.length === 0 && (
            <div className="p-4 text-ink-400">
              Brak przypisanych warstw dla {studyType}. Dodaj pierwszą lub wybierz inny typ.
            </div>
          )}
          {rows && rows.length > 0 && (
            <table className="table-hw">
              <thead>
                <tr>
                  <th className="w-20">Sort</th>
                  <th>Warstwa</th>
                  <th className="w-24">Protokół</th>
                  <th className="w-32">Kategoria</th>
                  <th className="w-28">Wymagana</th>
                  <th>Uwaga</th>
                  <th className="w-20">Status</th>
                  <th className="w-20"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.id} className={r.active ? '' : 'opacity-50'}>
                    <td className="font-mono text-[11px] text-ink-500">{r.sortOrder}</td>
                    <td className="text-ink-900 max-w-[320px]">
                      <div>{r.layerName}</div>
                      <div className="font-mono text-[11px] text-ink-500">{r.layerCode} · {r.layerSource}</div>
                    </td>
                    <td><span className="pill pill-earth">{r.layerProtocol}</span></td>
                    <td className="text-[11px] text-ink-500">{r.layerCategory}</td>
                    <td>
                      <button
                        onClick={() => onToggleField(r, { mandatory: !r.mandatory })}
                        className={r.mandatory ? 'pill pill-amber' : 'pill'} title="Kliknij, aby przełączyć">
                        {r.mandatory ? 'obowiązkowa' : 'zalecana'}
                      </button>
                    </td>
                    <td className="text-[11px] text-ink-500">
                      <div className="max-w-[240px] truncate" title={r.note ?? ''}>{r.note ?? '—'}</div>
                    </td>
                    <td>{r.active
                      ? <span className="pill pill-green">aktywna</span>
                      : <span className="pill pill-amber">usunięta</span>}</td>
                    <td className="text-right whitespace-nowrap">
                      {r.active && (
                        <button onClick={() => onDeactivate(r)} className="text-red-500 hover:text-red-700 p-1" title="Usuń przypisanie">
                          <Icon name="x" className="w-3.5 h-3.5"/>
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {creating && (
        <AssignLayerModal
          studyType={studyType}
          catalog={catalog}
          catalogErr={catalogErr}
          existingLayerIds={new Set((rows ?? []).map(r => r.mapLayerId))}
          onClose={() => setCreating(false)}
          onSaved={() => { setCreating(false); reload(); }}
        />
      )}
    </div>
  );
}

function AssignLayerModal({ studyType, catalog, catalogErr, existingLayerIds, onClose, onSaved }: {
  studyType: string;
  catalog: MapLayer[];
  catalogErr: boolean;
  existingLayerIds: Set<number>;
  onClose: () => void;
  onSaved: () => void;
}) {
  const available = useMemo(
    () => catalog.filter(l => !existingLayerIds.has(l.id))
                 .sort((a, b) => a.name.localeCompare(b.name, 'pl')),
    [catalog, existingLayerIds]);
  const [form, setForm] = useState<StudyTypeRequiredLayerInput>({
    studyType,
    mapLayerId: available[0]?.id ?? 0,
    mandatory: true,
    note: '',
    sortOrder: 100,
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = async () => {
    if (!form.mapLayerId) { setErr('Wybierz warstwę'); return; }
    setBusy(true); setErr(null);
    try { await studyTypeRequiredLayers.create({ ...form, studyType }); onSaved(); }
    catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  };

  const { requestClose, confirmation, dialogProps } = useUnsavedChanges({
    value: form, onClose, busy,
  });

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4 overflow-auto" onClick={requestClose} {...dialogProps}>
      {confirmation}
      <div className="bg-white rounded-lg shadow-2xl max-w-[600px] w-full mt-8" onClick={e => e.stopPropagation()}>
        <div className="border-b border-ink-100 px-5 py-3 flex items-center gap-3">
          <h2 className="text-[16px] font-semibold m-0">Przypisz warstwę do {studyType}</h2>
          <button onClick={requestClose} className="ml-auto text-ink-400 hover:text-ink-700"><Icon name="x"/></button>
        </div>

        <div className="p-5 space-y-3">
          {err && <div className="text-red-500 text-[12px]">{err}</div>}
          {available.length === 0 && (
            <div className="text-ink-400 text-[12px]">
              {catalogErr
                ? 'Nie udało się wczytać bazy warstw WMS/WMTS — odśwież stronę i spróbuj ponownie.'
                : 'Wszystkie warstwy z bazy są już przypisane do tego typu.'}
            </div>
          )}

          <Field label="Warstwa WMS/WMTS*">
            <SearchableSelect value={String(form.mapLayerId)}
                    onChange={v => setForm({ ...form, mapLayerId: Number(v) })}>
              {available.map(l => (
                <option key={l.id} value={String(l.id)}>{l.name} · {l.protocol} · {l.category}</option>
              ))}
            </SearchableSelect>
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Wymagalność">
              <SearchableSelect value={form.mandatory ? 'true' : 'false'}
                      onChange={v => setForm({ ...form, mandatory: v === 'true' })}>
                <option value="true">Obowiązkowa</option>
                <option value="false">Zalecana</option>
              </SearchableSelect>
            </Field>
            <Field label="Kolejność sortowania">
              <input type="number" value={form.sortOrder ?? 100}
                     onChange={e => setForm({ ...form, sortOrder: Number(e.target.value) })}
                     className="input font-mono"/>
            </Field>
          </div>

          <Field label="Uwaga (cel / podstawa)">
            <input value={form.note ?? ''} onChange={e => setForm({ ...form, note: e.target.value })}
                   placeholder="np. Ortofotomapa — podkład lokalizacyjny" className="input"/>
          </Field>
        </div>

        <div className="border-t border-ink-100 px-5 py-3 flex justify-end gap-2">
          <button onClick={requestClose} disabled={busy} className="btn btn-secondary btn-sm">Anuluj</button>
          <button onClick={save} disabled={busy || available.length === 0} className="btn btn-primary btn-sm">
            {busy ? 'Zapisywanie…' : 'Przypisz'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[10px] font-semibold uppercase tracking-wider text-ink-500">{label}</span>
      {children}
    </div>
  );
}
